"use client";

import {
	createContext,
	useCallback,
	useContext,
	useLayoutEffect,
	useMemo,
	useState,
	type ReactNode
} from "react";
import {
	Configure,
	InstantSearch,
	useClearRefinements,
	useHits,
	useInstantSearch,
	useRange,
	useRefinementList,
	useSortBy,
	useToggleRefinement
} from "react-instantsearch";

import { searchCategoryListing } from "@/app/actions/category-listing";
import type { ProductCardViewModel } from "@/app/components/shared/product-card";
import { ProductCardWithCart } from "@/app/components/shared/product-card/ProductCardWithCart";
import {
	FilterChips,
	FilterPanel,
	RANGE_VALUE,
	SortControl,
	selectAppliedFilters,
	type FilterGroup,
	type FilterPanelPresentation,
	type FilterRange
} from "@/composites/product-listing";
import { ProductGrid } from "@/composites/product";
import {
	LISTING_TOGGLE_VALUES,
	type ListingFacetAttribute
} from "@/domain/product-listing/listing-facets";

import type { ListingFilterDefinition } from "../category-content";
import type { CategoryListing } from "../get-category-page-data";
import {
	NO_SELECTION,
	toFilterGroups,
	type FacetCounts,
	type FacetSelection,
	type FacetStats,
	type RangeFormat
} from "../listing-filters";
import {
	createListingRouting,
	createListingSearchClient,
	LISTING_FACET_LIMIT,
	LISTING_INDEX,
	LISTING_PAGE_SIZE,
	LISTING_SORT_OPTIONS
} from "../listing-search";

type ListingHit = ProductCardViewModel & { objectID: string };

interface ListingStateValue {
	groups: FilterGroup[];
	cards: ProductCardViewModel[];
	total: number;
	isPending: boolean;
	sort: string;
	toggle: (groupId: string, value: string) => void;
	setRange: (groupId: string, range: FilterRange) => void;
	setSort: (sort: string) => void;
	clearAll: () => void;
}

const ListingStateContext = createContext<ListingStateValue | null>(null);

function useListingState(): ListingStateValue {
	const value = useContext(ListingStateContext);
	if (!value) {
		throw new Error(
			"Listing controls must sit inside ListingStateProvider"
		);
	}
	return value;
}

const CURRENCY_BY_LOCALE: Record<string, string> = {
	"en-US": "USD",
	"en-CA": "CAD"
};

function toRangeFormat(locale: string): RangeFormat {
	return {
		locale,
		formatOptions: {
			style: "currency",
			currency: CURRENCY_BY_LOCALE[locale] ?? "USD",
			maximumFractionDigits: 0
		}
	};
}

export interface ListingStateProviderProps {
	categoryCode: string;
	locale: string;
	initial: CategoryListing;
	children: ReactNode;
}

export function ListingStateProvider({
	categoryCode,
	locale,
	initial,
	children
}: ListingStateProviderProps): JSX.Element {
	const searchClient = useMemo(
		() =>
			createListingSearchClient((searches) =>
				searchCategoryListing({ categoryCode, locale, searches })
			),
		[categoryCode, locale]
	);
	const attributes = useMemo(
		() => [...new Set(initial.filters.map((filter) => filter.attribute))],
		[initial.filters]
	);
	const routing = useMemo(
		() => createListingRouting(attributes),
		[attributes]
	);
	const rangeFormat = useMemo(() => toRangeFormat(locale), [locale]);
	// InstantSearch renders nothing before it starts, so show the server's page.
	const [searchStarted, setSearchStarted] = useState(false);
	const markStarted = useCallback(() => setSearchStarted(true), []);

	return (
		<>
			<InstantSearch
				searchClient={searchClient}
				indexName={LISTING_INDEX}
				routing={routing}
				future={{ preserveSharedStateOnUnmount: true }}
			>
				<Configure hitsPerPage={LISTING_PAGE_SIZE} />
				<ListingWidgets filters={initial.filters} />
				<ListingFacets
					initial={initial}
					rangeFormat={rangeFormat}
					onStart={markStarted}
				>
					{children}
				</ListingFacets>
			</InstantSearch>
			{!searchStarted && (
				<ListingStateContext.Provider
					value={toServerState(initial, rangeFormat)}
				>
					{children}
				</ListingStateContext.Provider>
			)}
		</>
	);
}

function noop(): void {}

function toServerState(
	initial: CategoryListing,
	rangeFormat: RangeFormat
): ListingStateValue {
	return {
		groups: toFilterGroups(
			initial.filters,
			{
				total: initial.total,
				counts: initial.facets,
				stats: initial.stats
			},
			NO_SELECTION,
			rangeFormat
		),
		cards: initial.cards,
		total: initial.total,
		isPending: false,
		sort: LISTING_INDEX,
		toggle: noop,
		setRange: noop,
		setSort: noop,
		clearAll: noop
	};
}

function ListingWidgets({
	filters
}: {
	filters: ListingFilterDefinition[];
}): JSX.Element {
	const attributes = new Map(
		filters.map((filter) => [filter.attribute, filter.kind])
	);
	return (
		<>
			{[...attributes].map(([attribute, kind]) => {
				switch (kind) {
					case "list":
						return (
							<ListWidget key={attribute} attribute={attribute} />
						);
					case "toggle":
						return (
							<ToggleWidget
								key={attribute}
								attribute={attribute}
							/>
						);
					case "range":
						return (
							<RangeWidget
								key={attribute}
								attribute={attribute}
							/>
						);
				}
			})}
			<SortWidget />
		</>
	);
}

interface WidgetProps {
	attribute: ListingFacetAttribute;
}

function ListWidget({ attribute }: WidgetProps): null {
	useRefinementList({ attribute, limit: LISTING_FACET_LIMIT });
	return null;
}

function ToggleWidget({ attribute }: WidgetProps): null {
	useToggleRefinement({
		attribute,
		on: [...(LISTING_TOGGLE_VALUES[attribute] ?? [])]
	});
	return null;
}

function RangeWidget({ attribute }: WidgetProps): null {
	useRange({ attribute });
	return null;
}

function SortWidget(): null {
	useSortBy({ items: LISTING_SORT_OPTIONS });
	return null;
}

function parseRange(value: string | undefined): FilterRange {
	const [min, max] = (value ?? "")
		.split(":")
		.map((part) => (part === "" ? undefined : Number(part)));
	return {
		min: Number.isFinite(min) ? min : undefined,
		max: Number.isFinite(max) ? max : undefined
	};
}

function ListingFacets({
	initial,
	rangeFormat,
	onStart,
	children
}: {
	initial: CategoryListing;
	rangeFormat: RangeFormat;
	onStart: () => void;
	children: ReactNode;
}): JSX.Element {
	useLayoutEffect(onStart, [onStart]);

	const { items: hits } = useHits<ListingHit>();
	const { refine: clearAll } = useClearRefinements();
	const { results, status, indexUiState, setIndexUiState } = useInstantSearch(
		{ catchError: true }
	);
	const hasResults = !results.__isArtificial;

	const counts: FacetCounts = {};
	const stats: FacetStats = {};
	for (const filter of initial.filters) {
		const values = results.getFacetValues(filter.attribute, {}) as
			{ name: string; count: number }[] | undefined;
		counts[filter.attribute] = Object.fromEntries(
			(Array.isArray(values) ? values : []).map((value) => [
				value.name,
				value.count
			])
		);
		const facetStats = results.getFacetStats(filter.attribute);
		if (facetStats) stats[filter.attribute] = facetStats;
	}

	const selection: FacetSelection = {
		lists: indexUiState.refinementList ?? {},
		toggles: indexUiState.toggle ?? {},
		ranges: Object.fromEntries(
			Object.entries(indexUiState.range ?? {}).map(
				([attribute, value]) => [attribute, parseRange(value)]
			)
		)
	};

	function filterById(groupId: string): ListingFilterDefinition | undefined {
		return initial.filters.find((filter) => filter.id === groupId);
	}

	function toggle(groupId: string, value: string): void {
		const filter = filterById(groupId);
		if (!filter) return;
		const { attribute } = filter;
		setIndexUiState((state) => {
			if (filter.kind === "toggle") {
				return {
					...state,
					page: undefined,
					toggle: {
						...state.toggle,
						[attribute]: !state.toggle?.[attribute]
					}
				};
			}
			if (filter.kind === "range" && value === RANGE_VALUE) {
				return {
					...state,
					page: undefined,
					range: { ...state.range, [attribute]: "" }
				};
			}
			const current = state.refinementList?.[attribute] ?? [];
			const next = current.includes(value)
				? current.filter((entry) => entry !== value)
				: [...current, value];
			return {
				...state,
				page: undefined,
				refinementList: { ...state.refinementList, [attribute]: next }
			};
		});
	}

	function setRange(groupId: string, range: FilterRange): void {
		const filter = filterById(groupId);
		if (!filter) return;
		const value = `${range.min ?? ""}:${range.max ?? ""}`;
		setIndexUiState((state) => ({
			...state,
			page: undefined,
			range: {
				...state.range,
				[filter.attribute]: value === ":" ? "" : value
			}
		}));
	}

	function setSort(sort: string): void {
		setIndexUiState((state) => ({
			...state,
			page: undefined,
			sortBy: sort
		}));
	}

	const value: ListingStateValue = hasResults
		? {
				groups: toFilterGroups(
					initial.filters,
					{ total: results.nbHits, counts, stats },
					selection,
					rangeFormat
				),
				cards: hits,
				total: results.nbHits,
				isPending: status === "loading" || status === "stalled",
				sort: indexUiState.sortBy ?? LISTING_INDEX,
				toggle,
				setRange,
				setSort,
				clearAll
			}
		: {
				...toServerState(initial, rangeFormat),
				toggle,
				setRange,
				setSort,
				clearAll
			};

	return (
		<ListingStateContext.Provider value={value}>
			{children}
		</ListingStateContext.Provider>
	);
}

export function useListingTotal(): number {
	return useListingState().total;
}

export interface ListingFiltersProps {
	presentation: FilterPanelPresentation;
}

export function ListingFilters({
	presentation
}: ListingFiltersProps): JSX.Element | null {
	const { groups, toggle, setRange } = useListingState();

	return (
		<FilterPanel
			groups={groups}
			presentation={presentation}
			onValueChange={(change) => toggle(change.groupId, change.value)}
			onRangeChange={({ groupId, ...range }) => setRange(groupId, range)}
		/>
	);
}

export function ListingAppliedFilters(): JSX.Element | null {
	const { groups, toggle, clearAll } = useListingState();

	return (
		<FilterChips
			applied={selectAppliedFilters(groups)}
			onRemove={(filter) => toggle(filter.groupId, filter.value)}
			onClearAll={clearAll}
		/>
	);
}

export function ListingSort(): JSX.Element | null {
	const { sort, setSort } = useListingState();

	return (
		<SortControl
			options={LISTING_SORT_OPTIONS}
			selected={sort}
			onChange={setSort}
		/>
	);
}

export interface ListingResultsProps {
	className?: string;
	children: ReactNode;
}

export function ListingResults({
	className,
	children
}: ListingResultsProps): JSX.Element {
	const { isPending } = useListingState();

	return (
		<div
			className={className}
			aria-busy={isPending}
			data-pending={isPending || undefined}
		>
			{children}
		</div>
	);
}

export function ListingGrid(): JSX.Element {
	const { cards } = useListingState();

	return (
		<ProductGrid>
			{cards.map(({ product, statusLabel }) => (
				<ProductCardWithCart
					key={product.sku}
					product={product}
					source="plp"
					statusLabel={statusLabel}
				/>
			))}
		</ProductGrid>
	);
}
